import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Utils } from '@klickdata/core/util';
import { filter, map, Observable, of, switchMap, takeUntil, tap } from 'rxjs';
import { Media, MediaData } from '../media.model';
import { MediaService } from '../media.service';
import { AppScope } from '@klickdata/core/resource';
import { MatDialog } from '@angular/material/dialog';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { MaterialModelType } from '@klickdata/core/resource/src/resource.model';
export type MediaLoadType = string | number | Array<number> | Media | MediaData | Observable<Media>;

@Component({
    selector: 'app-image-view',
    templateUrl: './image-view.component.html',
    styleUrls: ['./image-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageViewComponent extends OnDestroyHandler implements OnInit, OnChanges {
    @Input() media_id: MediaLoadType;
    @Input() paddingBottom = 100 / (16 / 9) + '%';
    @Input() proportional = true;
    @Input() activateOverLay: boolean;
    @Input() smallSpinner = false;
    @Input() ignoreThumbnail = false;
    @Input() isCircularImg = false;
    @Input() showImgModalOnClick = true;
    @Input() backgroundSize = 'cover';
    /**@deprecated */
    @Input() scope: AppScope;
    @Output() srcLoaded: EventEmitter<string> = new EventEmitter<string>();
    @Output() onSrcError: EventEmitter<Media> = new EventEmitter<Media>();
    @Output() onAiPromptExist: EventEmitter<string> = new EventEmitter<string>();
    @Output() onCloseImgModal: EventEmitter<boolean> = new EventEmitter<boolean>();
    public media$: Observable<Media>;
    private isMobile: boolean;
    /**
     * TO display a circular image, paddingBottom should be passed
     * and isCircularImg to be true and add the ngStyle to the host with the
     * paddingBottom value like this example
     * <app-image-view
       [ngStyle]="{ minWidth: '50px' }"
       [isCircularImg]="true"
       [media_id]="user.media"
       paddingBottom="50px"
       ></app-image-view>
     */

    constructor(
        protected mediaService: MediaService,
        protected dialog: MatDialog,
        protected mobileService: MobileService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.mobileService
            .isMobile()
            .pipe(takeUntil(this.destroy))
            .subscribe((isMobile) => (this.isMobile = isMobile));
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if ('media_id' in changes) {
            const currentValue = changes['media_id'].currentValue as MediaLoadType;
            const previousValue = changes['media_id'].previousValue as MediaLoadType;
            if (currentValue && !Utils.isEqual(previousValue, currentValue)) {
                this.loadMediaOnce(currentValue);
            }
        }
    }

    protected loadMediaOnce(value: MediaLoadType) {
        if (value instanceof Observable) {
            this.media$ = value;
        } else if (value instanceof Media) {
            this.media$ = of(value);
        } else if (value.hasOwnProperty('type') && value.hasOwnProperty('path')) {
            this.media$ = of(new Media(<MediaData>value));
        } else if (
            !Array.isArray(value) &&
            typeof value === 'object' &&
            (value.src || value.url || value.default_url || value.fallback_url)
        ) {
            this.media$ = of(new Media(value));
        } else if (typeof value === 'string') {
            this.media$ = of(Utils.getMedia(value));
        } else if (typeof value === 'number') {
            this.media$ = this.mediaService.getMedia(value, this.ignoreThumbnail ? 'large' : null);
        } else if (Array.isArray(value) && !Utils.isEmpty(value)) {
            this.media$ = this.mediaService.getMedia(value[0], this.ignoreThumbnail ? 'large' : null);
        }
        this.media$ = this.media$.pipe(
            tap((media) => {
                if (!!media && !!media.type && media.type.indexOf('gpt') !== -1 && !!media.filename)
                    this.onAiPromptExist.emit(media.filename);
            })
        );
    }

    public onError(item: Media) {
        if (item.thumbnail && !this.ignoreThumbnail) {
            item.thumbnail = null;
        } else if (item.url) {
            item.url = null;
        } else if (item.src) {
            item.src = null;
        } else if (item.default_url) {
            item.default_url = null;
        }
    }
    public getItemImg(item: Media) {
        return this.ignoreThumbnail
            ? item.url || item.src || item.default_url || item.fallback_url
            : item.thumbnail || item.url || item.src || item.default_url || item.fallback_url;
    }
    public showImgModal(media: Media) {
        if (this.showImgModalOnClick) {
            this.dialog
                .open(DialogDisplayImgComponent, {
                    maxWidth: this.isMobile ? '90%' : '70%',
                    maxHeight: '90vh',
                    data: media,
                })
                .afterClosed()
                .pipe(
                    switchMap((result) => (result?.type === 'edit' ? this.onEditImg(result?.media) : of(result))),
                    takeUntil(this.destroy)
                )
                .subscribe(() => {
                    this.onCloseImgModal.emit(true);
                });
        }
    }
    onEditImg(media: Media): Observable<Media> {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_IMAGE_TO_LIST,
            data: {
                type: 'editImage',
                mediaType: $localize`Image`,
                media: media,
            },
        });
        return this.mobileService.getSideNavAction().pipe(
            filter((action) => action === SideNaveActionsTypes.POSITIVE),
            switchMap(() => this.mobileService.getSideNavResponseData()),
            filter((data) => data.type === 'editImage'),
            takeUntil(this.destroy),
            map((data) => data.value[0])
        );
    }
    public onClick(event: Event, item: Media) {
        if (!this.showImgModalOnClick) return;
        event.stopPropagation();
        this.showImgModal(item);
    }
}
